import { useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { FormHeaderWithStatus } from '@/components/layout/form-header-with-status';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { SentryDropdown } from '@/components/ui/sentry-dropdown';
import { TariffAutocomplete } from '@/components/ui/tariff-autocomplete';
import { LimitAutocomplete } from '@/components/ui/limit-autocomplete';
import { ScenarioAutocomplete } from '@/components/ui/scenario-autocomplete';
import { FormActionButtons } from '@/components/layout/form-action-buttons';
import { PriorityInput, type PriorityItem } from '@/components/ui/priority-input';
import type { UseFormReturn } from 'react-hook-form';
import type { PricingPolicyFormValues } from './pricing-policy-input';

interface BillingCycleOption {
	billingCycleId: number;
	name: string;
}

interface PricingPolicyFormProps {
	mode: 'create' | 'edit' | 'clone';
	form: UseFormReturn<PricingPolicyFormValues>;
	billingCycles: BillingCycleOption[];
	existingPriorities?: PriorityItem[];
	onSubmit: (values: PricingPolicyFormValues) => Promise<void> | void;
	isSubmitting: boolean;
	initialTariffLabel?: string;
	initialOverstayTariffLabel?: string;
	initialLimitLabel?: string;
	initialScenarioLabel?: string;
	headerTitle: string;
	onBack?: () => void;
}

export const PricingPolicyForm = ({
	mode,
	form,
	billingCycles,
	existingPriorities = [],
	onSubmit,
	isSubmitting,
	initialTariffLabel,
	initialOverstayTariffLabel,
	initialLimitLabel,
	initialScenarioLabel,
	headerTitle,
	onBack
}: PricingPolicyFormProps) => {
	const { t } = useTranslation('pricingPolicies');
	const nameInputRef = useRef<HTMLInputElement>(null);

	// Auto-focus name field when creating a new pricing policy
	useEffect(() => {
		if (mode === 'create') {
			setTimeout(() => {
				nameInputRef.current?.focus();
			}, 0);
		}
	}, [mode]);

	return (
		<>
			<FormHeaderWithStatus
				title={headerTitle}
				onBack={onBack}
				control={form.control}
				rightContent={
					<FormActionButtons
						mode={mode}
						isSubmitting={isSubmitting}
						validationErrors={form.formState.errors}
						formId="pricing-policy-form"
					/>
				}
			/>
			<Form {...form}>
				<form id="pricing-policy-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
					<Card className="space-y-6">
						<div className="space-y-1">
							<h2 className="text-lg font-semibold">
								{t('form.generalInfo')}
							</h2>
						</div>

					<div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
						<FormField
							control={form.control}
							name="name"
							rules={{ required: t('form.nameRequired') }}
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.name')}</FormLabel>
									<FormControl>
										<Input {...field} ref={nameInputRef} autoComplete="off" />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="priority"
							rules={{
								required: t('form.priorityRequired'),
								validate: value => {
									const num = parseInt(value, 10);
									if (isNaN(num)) return t('form.priorityWholeNumber');
									if (num < 0 || num > 1000) return t('form.priorityRange');
									return true;
								}
							}}
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.priority')}</FormLabel>
									<FormControl>
										<PriorityInput
											value={field.value}
											onChange={field.onChange}
											existingItems={existingPriorities}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="scenarioId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.scenario')}</FormLabel>
									<FormControl>
										<ScenarioAutocomplete
											value={field.value || ''}
											onChange={scenarioId =>
												field.onChange(scenarioId)
											}
											placeholder={t(
												'form.scenarioPlaceholder'
											)}
											className="w-full"
											enableCreateNew={true}
											initialSelectedLabel={
												initialScenarioLabel
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					</Card>

					<Card className="space-y-6">
						<div className="space-y-1">
							<h2 className="text-lg font-semibold">
								{t('form.tariffsSection')}
							</h2>
						</div>

					<div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
						<FormField
							control={form.control}
							name="tariffId"
							rules={{ required: 'Primary tariff is required.' }}
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('form.primaryTariff')}
									</FormLabel>
									<FormControl>
										<TariffAutocomplete
											value={field.value || ''}
											onChange={tariffId =>
												field.onChange(tariffId)
											}
											placeholder={t(
												'form.primaryTariffPlaceholder'
											)}
											className="w-full"
											enableCreateNew={true}
											initialSelectedLabel={
												initialTariffLabel
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="overstayTariffId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('form.overstayTariff')}
									</FormLabel>
									<FormControl>
										<TariffAutocomplete
											value={field.value || ''}
											onChange={tariffId =>
												field.onChange(tariffId)
											}
											placeholder={t(
												'form.overstayTariffPlaceholder'
											)}
											className="w-full"
											enableCreateNew={true}
											initialSelectedLabel={
												initialOverstayTariffLabel
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					</Card>

					<Card className="space-y-6">
						<div className="space-y-1">
							<h2 className="text-lg font-semibold">
								{t('form.limitsAndBillingSection')}
							</h2>
						</div>

					<div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
						<FormField
							control={form.control}
							name="limitId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('form.limit')}</FormLabel>
									<FormControl>
										<LimitAutocomplete
											value={field.value || ''}
											onChange={limitId =>
												field.onChange(limitId)
											}
											placeholder={t(
												'form.limitPlaceholder'
											)}
											className="w-full"
											enableCreateNew={true}
											initialSelectedLabel={
												initialLimitLabel
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="billingCycleId"
							rules={{ required: 'Billing cycle is required.' }}
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('form.billingCycle')}
									</FormLabel>
									<FormControl>
										<SentryDropdown
											options={billingCycles.map(
												cycle => ({
													value: String(
														cycle.billingCycleId
													),
													label: cycle.name
												})
											)}
											value={field.value || ''}
											onChange={value =>
												field.onChange(value)
											}
											placeholder={t(
												'form.billingCyclePlaceholder'
											)}
											disabled={!billingCycles.length}
											searchable
											className="w-full"
											autoSelectFirstOption={true}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
					</Card>
			</form>
		</Form>
		</>
	);
};
